import './styles.scss';

import { QuestionnaireAnswerType, QuestionnaireCategory, QuestionnaireQuestion } from '@/models/gen/graphql';
import { createNotification, handleError } from '@/utils/custom';

import Form from '@/common/Form';
import Modal from '@/common/Modal';
import { ModalTypes } from '@/common/Modal/types';
import { QuestionFormDefinitions } from '@/features/Settings/pages/Questions/components/QuestionModal/types';
import Select from '@/common/Select';
import Textarea from '@/common/Textarea';
import { Toast } from '@/models';
import { createQuestion } from '@/features/Settings/api/services/training/createQuestionnaireQuestions';
import { deleteQuestion } from '@/features/Settings/api/services/training/deleteQuestionnaireQuestions';
import { updateQuestion } from '@/features/Settings/api/services/training/updateQuestionnaireQuestions';
import useConfirmation from '@/hooks/useConfirmation';
import { useMemo } from 'react';

type QuestionModalProps = Omit<ModalTypes.Props, 'title'> & {
  question?: QuestionnaireQuestion;
  categories: QuestionnaireCategory[];
  answerTypes: QuestionnaireAnswerType[];
  defaultCategoryId?: string;
  onSubmit: () => void;
};

const QuestionModal = ({
  show,
  question,
  categories,
  answerTypes,
  defaultCategoryId,
  onHide,
  onSubmit,
  ...modalProps
}: QuestionModalProps): JSX.Element => {
  const isEdit = Boolean(question?.id);

  const confirmDelete = useConfirmation({
    Body: {
      title: 'Delete Training Item',
      message: 'Are you sure you want to delete this training item? It will no longer be available for new evaluations.',
    },
    Footer: { resolve: 'DELETE PERMANENTLY', reject: 'CANCEL' },
  });

  const value = useMemo(
    (): QuestionFormDefinitions.Value =>
      isEdit && question
        ? {
            helperText: question.helperText,
            questionnaireCategoryId: question.questionnaireCategoryId,
            questionText: question.questionText,
            answerTypeId: question.answerTypeId,
          }
        : { ...QuestionFormDefinitions.initValue, questionnaireCategoryId: defaultCategoryId || '' },
    [isEdit, question, defaultCategoryId]
  );

  const handleSubmit = async (data: QuestionFormDefinitions.Value): Promise<void> => {
    const title = isEdit ? 'Update Training Item' : 'Create Training Item';
    try {
      if (isEdit) {
        await updateQuestion([
          {
            id: question.id,
            helperText: data.helperText,
            questionnaireCategoryId: data.questionnaireCategoryId,
            questionText: data.questionText,
            answerTypeId: data.answerTypeId,
          },
        ]);
      } else {
        await createQuestion({
          helperText: data.helperText,
          questionnaireCategoryId: data.questionnaireCategoryId,
          questionText: data.questionText,
          answerTypeId: data.answerTypeId,
        });
      }
      createNotification('Success', Toast.Type.SUCCESS, title);
      onSubmit();
      onHide();
    } catch (err) {
      handleError(err, { notification: { title } });
    }
  };

  const handleDelete = async (): Promise<void> => {
    try {
      await confirmDelete();
    } catch {
      return;
    }
    try {
      await deleteQuestion([question.id]);
      createNotification('Success', Toast.Type.SUCCESS, 'Delete Training Item');
      onSubmit();
      onHide();
    } catch (err) {
      handleError(err, { notification: { title: 'Delete Training Item' } });
    }
  };

  return (
    <Modal
      {...modalProps}
      show={show}
      onHide={onHide}
      title={isEdit ? 'Edit Training Item' : 'Create Training Item'}
      centered
      className="QuestionModal"
    >
      <Form
        name="questionModal"
        className="QuestionModal-Form"
        value={value}
        validator={QuestionFormDefinitions.validator}
        onSubmit={handleSubmit}
      >
        <Form.Field name="helperText" label="Description">
          <Form.Input name="helperText" validate />
        </Form.Field>
        <Form.Field name="questionnaireCategoryId" label="Category">
          <Form.Input as={Select} name="questionnaireCategoryId" validate>
            {categories.map((category) => (
              <Select.Option key={category.id} value={category.id}>
                {category.name}
              </Select.Option>
            ))}
          </Form.Input>
        </Form.Field>
        <Form.Field name="questionText" label="Checklist Question">
          <Form.Input as={Textarea} name="questionText" rows={4} validate />
        </Form.Field>
        <Form.Field name="answerTypeId" label="Question Type">
          <Form.Input as={Select} name="answerTypeId" validate>
            {answerTypes.map((answerType) => (
              <Select.Option key={answerType.id} value={answerType.id}>
                {answerType.displayName}
              </Select.Option>
            ))}
          </Form.Input>
        </Form.Field>
        <div className="QuestionModal-Footer">
          <Form.Button type="button" variant="secondary" onClick={onHide}>
            Cancel
          </Form.Button>
          {isEdit && (
            <Form.Button type="button" variant="danger" onClick={handleDelete}>
              Delete
            </Form.Button>
          )}
          <Form.Button type="submit" variant="primary">
            Save
          </Form.Button>
        </div>
      </Form>
    </Modal>
  );
};

export default QuestionModal;
